/**
 * Formatting helpers, status palette, and the KPI-strip / outcome-summary
 * sub-components shared between `Rsi2PullbackPage.tsx`'s `ChartDialog`
 * modal and `Rsi2ChartPanel.tsx` (the chart+KPI body reused by the
 * standalone `/investing-idea/rsi2-pullback/chart` full-page route). Lives
 * in its own module -- rather than being exported from `Rsi2PullbackPage.tsx`
 * directly and imported back by `Rsi2ChartPanel.tsx` -- specifically to
 * avoid a circular import between those two files.
 */
import { Chip, Stack, Typography } from "@mui/material";
import type {
  Rsi2PullbackChartResponse,
  Rsi2PullbackIdeaDetailResponse,
  Rsi2PullbackIdeaRow,
  Rsi2PullbackTargetStatus,
} from "@/types";

// Approved-terminology-only palette (spec: Research Candidate / Setup
// Detected / Target Hit / Expired / Data Unavailable) — deliberately a
// local copy rather than a shared module, matching this codebase's
// established per-consumer status-palette convention (see
// `MarketOpportunitiesPage.tsx`'s own `BREAKOUT_STATUS_COLORS`).
export const STATUS_COLORS: Record<Rsi2PullbackTargetStatus, string> = {
  PENDING_REFERENCE: "#64748B",
  PENDING: "#2563EB",
  TARGET_HIT: "#1B5E20",
  EXPIRED: "#9E9E9E",
  DATA_UNAVAILABLE: "#B71C1C",
};

export function StatusBadge({ status, label }: { status: Rsi2PullbackTargetStatus; label: string }) {
  return <Chip label={label} size="small" sx={{ bgcolor: STATUS_COLORS[status], color: "#fff", fontWeight: 600 }} />;
}

export function num(value: string | number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

/** "1 trading day" vs "N trading days" -- `days` may be null/undefined
 * (outcome not yet available), in which case the count itself renders as
 * "—" but the noun still needs a form, so this defaults to plural ("—
 * trading days") which reads naturally either way. */
export function tradingDaysLabel(days: number | null | undefined): string {
  const count = days ?? "—";
  const noun = days === 1 ? "trading day" : "trading days";
  return `${count} ${noun}`;
}

// Client-side range toggle (spec item 8): the chart-data API already
// returns AT MOST ~260 trading days (~1Y) before the signal date (see
// `chart_service.DISPLAY_LOOKBACK_BARS`, not touched here) plus whatever
// small observation/outcome period exists after it, so 1W/3M/6M/1Y can all
// be served by slicing the already-fetched `chart.candles`/`sma_50`/
// `sma_200`/`rsi2` arrays in the browser -- no extra fetch needed. A "2Y"
// option is deliberately OMITTED: honoring it would require a backend
// lookback change (out of scope here), and silently showing < 2 years of
// data mislabeled "2Y" would be misleading.
export const RANGE_BARS_BEFORE_SIGNAL: Record<"1W" | "3M" | "6M" | "1Y", number> = {
  "1W": 5, // ~1 trading week
  "3M": 65, // ~3 trading months
  "6M": 130, // ~6 trading months (default)
  "1Y": 260, // matches the backend's own DISPLAY_LOOKBACK_BARS cap
};
export type ChartRange = keyof typeof RANGE_BARS_BEFORE_SIGNAL;

export function windowChart(chart: Rsi2PullbackChartResponse, range: ChartRange) {
  const barsBefore = RANGE_BARS_BEFORE_SIGNAL[range];
  const signalIdx = chart.candles.findIndex((c) => c.trade_date === chart.signal_date);
  const anchor = signalIdx >= 0 ? signalIdx : chart.candles.length - 1;
  // Always include every bar after the signal (the observation/outcome
  // period) regardless of the selected range -- never trim off real
  // outcome data.
  const start = Math.max(0, anchor - barsBefore);
  return {
    candles: chart.candles.slice(start),
    sma50: chart.sma_50.slice(start),
    sma200: chart.sma_200.slice(start),
    rsi2: chart.rsi2.slice(start),
  };
}

export function ChartKpiStrip({ row }: { row: Rsi2PullbackIdeaRow }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
      <StatusBadge status={row.target_status} label={row.target_status_label} />
      <Chip variant="outlined" size="small" label={`Signal: ${formatDate(row.signal_date)}`} />
      <Chip
        variant="outlined"
        size="small"
        label={`Reference: ${row.reference_price ? `₹${num(row.reference_price)}` : "Pending"}`}
      />
      <Chip
        variant="outlined"
        size="small"
        label={`Target: ${row.indicative_target_price ? `₹${num(row.indicative_target_price)}` : "—"}${
          row.target_pct ? ` (+${num(row.target_pct)}%)` : ""
        }`}
      />
      <Chip variant="outlined" size="small" label={`RSI(2): ${num(row.rsi2_at_signal)}`} />
    </Stack>
  );
}

// Below-chart outcome summary (spec items 5/6/7): Target Hit shows the hit
// date + trading days to target (from `getDetail`'s outcome, not present on
// the lighter chart-data response); Pending shows observation-window
// progress computed only from already-visible `chart.candles` + the row's
// own `observation_days` (never fabricated/future data); Expired shows the
// observation-period close/high-water-mark actually reached.
export function ChartOutcomeSummary({
  row,
  chart,
  detail,
  detailLoading,
}: {
  row: Rsi2PullbackIdeaRow;
  chart: Rsi2PullbackChartResponse;
  detail: Rsi2PullbackIdeaDetailResponse | null;
  detailLoading: boolean;
}) {
  if (chart.target_status === "TARGET_HIT") {
    if (detailLoading || !detail) {
      return (
        <Typography variant="body2" color="text.secondary">
          Loading outcome…
        </Typography>
      );
    }
    return (
      <Typography variant="body2">
        Target Hit | {formatDate(detail.outcome.target_hit_date)} |{" "}
        {tradingDaysLabel(detail.outcome.trading_days_to_target)} to target
      </Typography>
    );
  }

  if (chart.target_status === "EXPIRED") {
    if (detailLoading || !detail) {
      return (
        <Typography variant="body2" color="text.secondary">
          Loading outcome…
        </Typography>
      );
    }
    return (
      <Typography variant="body2">
        Expired | Observation Period Close: ₹{num(detail.outcome.observation_period_close)} | Max Price Reached: ₹
        {num(detail.outcome.max_price_reached)}
        {detail.outcome.max_price_reached_date ? ` (${formatDate(detail.outcome.max_price_reached_date)})` : ""}
      </Typography>
    );
  }

  if (chart.target_status === "PENDING" || chart.target_status === "PENDING_REFERENCE") {
    // Trading days elapsed since the reference (fallback: signal) date, up
    // to the latest actually-ingested candle -- a simple count of already-
    // rendered bars, never a calendar-day subtraction and never implying
    // any bar beyond what the backend returned.
    const anchorDate = row.reference_date ?? chart.signal_date;
    const anchorIdx = chart.candles.findIndex((c) => c.trade_date === anchorDate);
    const elapsed = anchorIdx >= 0 ? Math.max(0, chart.candles.length - anchorIdx) : 0;
    const total = row.observation_days;
    return (
      <Typography variant="body2">
        Pending | Day {Math.min(elapsed, total)} of {total} observation days
      </Typography>
    );
  }

  return null;
}

/** Builds the URL for the standalone, full-page rendering of one idea's
 * chart (`Rsi2ChartStandalonePage.tsx`, registered in `App.tsx`) -- opened
 * via the "Open in new tab" button on `Rsi2ChartPanel`. */
export function rsi2ChartStandaloneUrl(symbol: string, signalDate: string): string {
  const params = new URLSearchParams({ symbol, signalDate });
  return `/investing-idea/rsi2-pullback/chart?${params.toString()}`;
}
